import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { Copy, Check, ExternalLink, KeyRound, Lock, Unlock } from 'lucide-react';
import API_ENDPOINTS from '../../config/api';
import useInstanceRecord from '../../hooks/useInstanceRecord';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import ConfirmDialog from '../ConfirmDialog';
import { useNotice } from '../DialogProvider';
import { media } from '../../styles/breakpoints';

// The run strip: what the facilitator needs at any moment of the workshop,
// at the right end of the stage row on every Toteutus page — the participant
// PIN for connecting devices, and the workshop lock.

const Strip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  flex-shrink: 0;
`;

const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 8px;
  border: 1px solid ${p => (p.$tone === 'locked' ? 'rgba(248, 113, 113, 0.4)' : '#333')};
  background: ${p => (p.$tone === 'locked' ? 'rgba(248, 113, 113, 0.12)' : 'rgba(255, 255, 255, 0.04)')};
  color: ${p => (p.$tone === 'locked' ? '#fca5a5' : '#ddd')};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) { background: rgba(255, 255, 255, 0.08); }
  &:disabled { opacity: 0.5; cursor: wait; }

  ${media.sm} { .wide { display: none; } }
`;

const PinCode = styled.span`
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 15px;
  letter-spacing: 0.12em;
  color: #fff;
`;

const PinWrap = styled.div`
  position: relative;
`;

const Popover = styled.div`
  position: fixed;
  top: calc(var(--kuutio-admin-header-height, 104px) + 6px);
  right: 16px;
  z-index: 160;
  min-width: 260px;
  max-width: calc(100vw - 32px);
  padding: 10px;
  background: rgba(0, 0, 0, 0.95);
  border: 1px solid #333;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.45);
`;

const PopoverTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 2px 4px 8px;
`;

const PinRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 4px;
  border-top: 1px solid #1f1f1f;
  font-size: 13px;
  color: #bbb;

  span:first-of-type { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; }
`;

const CopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid #333;
  background: transparent;
  color: #aaa;
  font-size: 11px;
  cursor: pointer;

  &:hover { color: #fff; }
`;

// A real link, so a right-click can open it in a private window: in this
// browser the admin's own sign-in still applies.
const OpenLink = styled.a`
  display: inline-flex;
  align-items: center;
  padding: 4px 6px;
  border-radius: 6px;
  border: 1px solid #333;
  color: #aaa;

  &:hover { color: #fff; }
`;

const Note = styled.div`
  font-size: 12px;
  color: #999;
  padding: 4px;
  line-height: 1.4;
`;

function usePins(instanceId) {
  const [state, setState] = useState({ pins: [], error: null });
  useEffect(() => {
    if (!instanceId) return undefined;
    let cancelled = false;
    fetch(API_ENDPOINTS.INSTANCE_PINS(instanceId), { credentials: 'include' })
      .then((res) => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json(); })
      .then((data) => {
        if (cancelled) return;
        // The most used PIN first: that is the one the room signs in with.
        const pins = [...(data.pins || [])].sort((a, b) => (b.usage_count || 0) - (a.usage_count || 0));
        setState({ pins, error: null });
      })
      .catch((err) => { if (!cancelled) setState({ pins: [], error: err }); });
    return () => { cancelled = true; };
  }, [instanceId]);
  return state;
}

export default function RunStrip({ instanceId }) {
  const { t } = useTranslation();
  const notice = useNotice();
  const { instance, setValue } = useInstanceRecord(instanceId);
  const { pins, error: pinsError } = usePins(instanceId);
  const [open, setOpen] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [confirmUnlock, setConfirmUnlock] = useState(false);
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef(null);
  const popoverRef = useRef(null);

  const locked = Boolean(instance?.settings?.locked);
  // The room signs in with the instance PIN (settings.pin, routes/auth.js);
  // personal PINs (auth_pins) follow, most used first.
  const instancePin = instance?.settings?.pin
    ? { id: 'instance', pin_code: instance.settings.pin, display_name: t('adminRunStrip.instancePin') }
    : null;
  const allPins = instancePin ? [instancePin, ...pins] : pins;
  const mainPin = allPins[0];

  useEffect(() => {
    if (!open) return undefined;
    const inside = (ref, e) => ref.current && ref.current.contains(e.target);
    const close = (e) => { if (!inside(wrapRef, e) && !inside(popoverRef, e)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const copy = async (pin) => {
    try {
      await navigator.clipboard.writeText(pin.pin_code);
      setCopiedId(pin.id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const persistLock = async (next) => {
    setBusy(true);
    try {
      await setValue('settings', 'locked', next);
    } catch (err) {
      notice.error(t('workshopOrchestrator.lockFailed', { error: err.message }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Strip>
      <PinWrap ref={wrapRef}>
        <Chip type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} title={t('adminRunStrip.pinTitle')}>
          <KeyRound size={14} />
          <span className="wide">{t('adminRunStrip.pinLabel')}</span>
          <PinCode>{mainPin ? mainPin.pin_code : '—'}</PinCode>
        </Chip>
        {/* Portalled: the stage row blurs and scrolls, which would clip it */}
        {open && createPortal(
          <Popover ref={popoverRef}>
            <PopoverTitle>{t('adminRunStrip.pinTitle')}</PopoverTitle>
            {pinsError && <Note>{t('adminRunStrip.pinsFailed', { error: pinsError.message })}</Note>}
            {!pinsError && allPins.length === 0 && <Note>{t('adminRunStrip.noPins')}</Note>}
            {allPins.map((pin) => (
              <PinRow key={pin.id}>
                <span>{pin.display_name}</span>
                <PinCode>{pin.pin_code}</PinCode>
                <CopyButton type="button" onClick={() => copy(pin)}>
                  {copiedId === pin.id ? <Check size={12} /> : <Copy size={12} />}
                  {copiedId === pin.id ? t('adminRunStrip.copied') : t('adminRunStrip.copy')}
                </CopyButton>
                <OpenLink
                  href={`/?pin=${encodeURIComponent(pin.pin_code)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={t('adminRunStrip.openParticipantView')}
                  aria-label={t('adminRunStrip.openParticipantView')}
                >
                  <ExternalLink size={12} />
                </OpenLink>
              </PinRow>
            ))}
          </Popover>,
          document.body,
        )}
      </PinWrap>

      <Chip
        type="button"
        $tone={locked ? 'locked' : 'open'}
        disabled={busy || !instance}
        title={locked ? t('workshopOrchestrator.lockedBar') : t('workshopOrchestrator.unlockedBar')}
        onClick={() => (locked ? setConfirmUnlock(true) : persistLock(true))}
      >
        {locked ? <Lock size={14} /> : <Unlock size={14} />}
        {locked ? t('adminRunStrip.locked') : t('adminRunStrip.open')}
        <span className="wide" style={{ opacity: 0.6, fontWeight: 500 }}>
          · {locked ? t('workshopOrchestrator.unlock') : t('workshopOrchestrator.lock')}
        </span>
      </Chip>

      {confirmUnlock && (
        <ConfirmDialog
          title={t('workshopOrchestrator.unlockTitle')}
          message={t('workshopOrchestrator.unlockMessage')}
          confirmLabel={t('workshopOrchestrator.unlock')}
          onConfirm={() => { setConfirmUnlock(false); persistLock(false); }}
          onCancel={() => setConfirmUnlock(false)}
        />
      )}
    </Strip>
  );
}
